import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type WikiKey = 'products' | 'inventoryCheck' | 'inventoryManage' | 'transfers' | 'history' | 'analytics' | 'cabinet' | 'users' | 'subscriptions';

export function accessibleWikiKeys(role: string | null): WikiKey[] {
  const common: WikiKey[] = ['products', 'inventoryCheck', 'inventoryManage', 'transfers', 'history', 'analytics', 'cabinet'];
  if (role === 'ROLE_ADMIN') return [...common, 'users', 'subscriptions'];
  if (role === 'ROLE_TRANSPORT') return ['transfers'];
  if (role === 'ROLE_INVENTORY') return ['inventoryCheck', 'inventoryManage', 'transfers'];
  return common;
}

export default function InformationPage({ role }: { role: string | null }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const sections = [
    ['📦', 'inventory'], ['🏢', 'structure'], ['🔄', 'transfers'],
    ['📊', 'analytics'], ['✉️', 'reports'], ['⚠️', 'alerts'],
    ['👥', 'roles'], ['💳', 'subscription']
  ] as const;
  const wikiTabs = [
    ['📋', 'products'], ['🧮', 'inventoryCheck'], ['📦', 'inventoryManage'],
    ['🔄', 'transfers'], ['🧾', 'history'], ['📊', 'analytics'],
    ['⚙️', 'cabinet'], ['👥', 'users'], ['💳', 'subscriptions']
  ] as const;
  const actions: Record<(typeof wikiTabs)[number][1], string[]> = {
    products: ['add', 'edit', 'delete', 'export', 'print', 'visibility'],
    inventoryCheck: ['scan', 'move', 'conclude', 'actualize', 'download'],
    inventoryManage: ['select', 'bulkAdd', 'bulkSell', 'quantity', 'lowStock'],
    transfers: ['dispatch', 'cancel', 'refresh'],
    history: ['filters', 'sales', 'stock', 'export'],
    analytics: ['period', 'company', 'user', 'charts'],
    cabinet: ['profile', 'companies', 'deposits', 'categories', 'reports', 'testEmail'],
    users: ['create', 'role', 'companies', 'deposits', 'activate', 'password'],
    subscriptions: ['status', 'days', 'infinite', 'history']
  };
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const accessibleKeys = accessibleWikiKeys(role);
  const visibleSections = role === 'ROLE_TRANSPORT'
    ? sections.filter(([, key]) => key === 'transfers')
    : role === 'ROLE_INVENTORY'
      ? sections.filter(([, key]) => ['inventory', 'transfers', 'alerts'].includes(key))
      : sections;
  const visibleWikiTabs = wikiTabs.filter(([, key]) => {
    if (!accessibleKeys.includes(key)) return false;
    if (!normalizedQuery) return true;
    const text = [t(`information.wiki.${key}.title`), t(`information.wiki.${key}.purpose`),
      ...actions[key].flatMap(action => [t(`information.wiki.${key}.actions.${action}.label`), t(`information.wiki.${key}.actions.${action}.description`)])]
      .join(' ').toLocaleLowerCase();
    return text.includes(normalizedQuery);
  });

  return (
    <main className="information-page">
      <header className="information-hero">
        <span>{t('information.eyebrow')}</span>
        <h1>{t('information.title')}</h1>
        <p>{t('information.introduction')}</p>
      </header>
      <section className="information-grid">
        {visibleSections.map(([icon, key], index) => (
          <article className="information-card" key={key}>
            <div className="information-card-icon">{icon}</div>
            <div>
              <small>{String(index + 1).padStart(2, '0')}</small>
              <h2>{t(`information.sections.${key}.title`)}</h2>
              <p>{t(`information.sections.${key}.description`)}</p>
            </div>
          </article>
        ))}
      </section>
      <aside className="information-tip">
        <strong>{t('information.quickStartTitle')}</strong>
        <p>{t('information.quickStart')}</p>
      </aside>
      <section className="wiki-section">
        <div className="wiki-heading">
          <div><span>{t('information.wikiEyebrow')}</span><h2>{t('information.wikiTitle')}</h2><p>{t('information.wikiIntroduction')}</p></div>
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t('information.searchPlaceholder')} aria-label={t('information.searchPlaceholder')} />
        </div>
        <div className="wiki-list">
          {visibleWikiTabs.map(([icon, key], index) => (
            <details className="wiki-topic" key={key} open={index === 0 && !normalizedQuery}>
              <summary><span className="wiki-topic-icon">{icon}</span><span><strong>{t(`information.wiki.${key}.title`)}</strong><small>{t(`information.wiki.${key}.purpose`)}</small></span><b>+</b></summary>
              <div className="wiki-topic-body">
                <div className="wiki-workflow"><strong>{t('information.workflow')}</strong><p>{t(`information.wiki.${key}.workflow`)}</p></div>
                <h3>{t('information.buttonsAndControls')}</h3>
                <div className="wiki-actions">
                  {actions[key].map(action => <div className="wiki-action" key={action}><strong>{t(`information.wiki.${key}.actions.${action}.label`)}</strong><p>{t(`information.wiki.${key}.actions.${action}.description`)}</p></div>)}
                </div>
              </div>
            </details>
          ))}
          {visibleWikiTabs.length === 0 && <p className="wiki-empty">{t('information.noResults')}</p>}
        </div>
      </section>
    </main>
  );
}
